import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { normalizeCurrency } from '@/lib/currency';
import {
  fetchMerchantFormations,
  formationPrice,
  type PublicFormation,
} from '@/lib/publicFormations';

interface FormationsTagProps {
  limit?: number;
}

/**
 * Balise {{formations}} — liste les formations publiées du commerce.
 * Chaque carte ouvre la fiche publique de la formation, où le visiteur
 * peut s'inscrire (gratuit) ou payer en ligne.
 */
export default function FormationsTag({ limit }: FormationsTagProps) {
  const { tenant, isTenant } = useTenant();
  const merchantId = tenant?.id ?? null;
  const currency = normalizeCurrency(tenant?.monaie || 'MAD');

  const [formations, setFormations] = useState<PublicFormation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isTenant || !merchantId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const rows = await fetchMerchantFormations(merchantId);
        if (cancelled) return;
        setFormations(limit && limit > 0 ? rows.slice(0, limit) : rows);
      } catch {
        if (!cancelled) setFormations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isTenant, merchantId, limit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
      </div>
    );
  }

  if (formations.length === 0) return null;

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold mb-3">
              <i className="ri-graduation-cap-line"></i>Formations
            </span>
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">Nos formations</h2>
            <p className="text-sm text-foreground-500 mt-2 max-w-xl">
              Apprenez à votre rythme : inscrirez-vous en un clic et retrouvez vos cours dans votre espace.
            </p>
          </div>
          <Link
            to="/formations"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 no-underline whitespace-nowrap"
          >
            Toutes les formations <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
          {formations.map((f) => {
            const price = formationPrice(f);
            return (
              <Link
                key={f.id}
                to={`/formation/${f.id}`}
                className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-background-300/60 transition-colors no-underline flex flex-col"
              >
                <div className="relative aspect-[16/10] bg-background-100 overflow-hidden">
                  {f.image ? (
                    <img
                      src={f.image}
                      alt={f.titre}
                      title={`${f.titre} — formation`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-secondary-50">
                      <i className="ri-play-circle-line text-5xl text-primary-300"></i>
                    </div>
                  )}
                  <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-background-50/95 text-foreground-800">
                    {price > 0 ? `${price.toLocaleString('fr-FR')} ${currency}` : 'Gratuit'}
                  </span>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {f.titre}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mt-1.5">
                    {f.description || 'Programme détaillé disponible sur la fiche.'}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap mt-3 text-[11px] text-foreground-500">
                    {f.categorie && (
                      <span className="px-2 py-0.5 rounded-full bg-background-100">{f.categorie}</span>
                    )}
                    {f.duree && (
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i>{f.duree}</span>
                    )}
                  </div>
                  <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600">
                    Voir la formation <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}